"use client";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Heading,
  IconButton,
  Tooltip,
  useToast,
  useDisclosure,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  Input,
  Button,
} from "@chakra-ui/react";
import {
  FaTruck,
  FaSync,
  FaTimes,
  FaTrash,
  FaRoute,
  FaMotorcycle,
  FaEye,
  FaUser,
  FaBox,
  FaMoneyBill,
  FaEllipsisH,
  FaFileInvoice,
  FaMapMarkerAlt,
  FaLink,
  FaPrint,
} from "react-icons/fa";
import {
  Shipment,
  ShipmentStatus,
  PorterDeliveryTrackingStatus,
  VehicleType,
} from "../types";
import {
  cancelShipment,
  deleteShipment,
  getShipment,
  getTracking,
  updateShipmentService,
  markConsignmentDeliveredService,
} from "../service";
import { GaraazCopyText } from "@svan-garaaz/reactsharedlib";
import GaraazAlertDialogue from "../../../components/GaraazAlertDialogue";
import {
  formatDateForNotification,
  formatTime,
} from "../../../utils/dateResolvers";
import { MdAccessTime } from "react-icons/md";
import TrackingModal, { TrackingDetails } from "./TrackingModal";
import { GetStatusBadge } from "./GetStatusBadge";
import { useGetMobileConfig } from "../../../components/queryHooks";
import ShipmentDetailsModal from "./ShipmentDetailsModal";
import type { ShipmentDetails } from "../types";
import { formatCurrency } from "../../../utils/util";
import { InvoiceStatus, DeliveryModes } from "../../../types";
import { FcShipped } from "react-icons/fc";
import DeliveryModeBadge from "../../../components/DeliveryModeBadge";
import {
  formatMultiStopDestinationAddress,
  getDeliveryStopStatus,
  getMultiCustomerLabel,
  getPrimaryDropAddress,
  isMultiCustomerTrip,
  isMultiStopTrip,
} from "../utils/logisticsTripUtils";
import {
  openWhatsAppWithLocation,
  formatAddress,
  generateLocationMessage,
  type OrderInvoicePair,
  type DestinationInfo,
} from "../../../utils/locationUtils";
import {
  InvoiceMediaIdentifier,
  printGaraazInvoices,
} from "../../../utils/invoicePrintUtils";
import { extractInvoiceMediaIdsFromShipment } from "../../../utils/invoiceExtractionUtils";
import {
  handleInvoicePrintError,
  showInvoicePrintSuccess,
} from "../../../utils/invoicePrintErrorHandler";

interface RawOrder {
  orderNo?: string;
  invoiceInfo?: {
    invoiceNo?: string;
  };
  challanInfo?: {
    challanNo?: string;
  };
}

function extractOrderInvoicePairs(shipment: Shipment): OrderInvoicePair[] {
  const orders = (shipment as any).orders as RawOrder[] | undefined;

  if (orders && Array.isArray(orders) && orders.length > 0) {
    return orders
      .filter((ord) => ord?.orderNo)
      .map((ord) => {
        const invoiceNo = ord.invoiceInfo?.invoiceNo;
        return {
          orderNumber: ord.orderNo!,
          invoiceNumber: invoiceNo && invoiceNo !== "-" ? invoiceNo : null,
        };
      });
  }

  const orderNums = shipment.orderNumbers || [];
  const invoiceNums = shipment.invoiceNumbers || [];

  return orderNums.map((orderNum, index) => ({
    orderNumber: orderNum,
    invoiceNumber:
      invoiceNums[index] && invoiceNums[index] !== "-"
        ? invoiceNums[index]
        : null,
  }));
}

function extractDestinationInfoFromShipment(shipment: Shipment): {
  destinationInfo: DestinationInfo;
  customerLat: number | undefined;
  customerLng: number | undefined;
} | null {
  const dropAddress = getPrimaryDropAddress(shipment);
  const pickupAddress = shipment.pickupDetails?.address;

  const customerLat = dropAddress?.lat;
  const customerLng = dropAddress?.lng;

  if (!customerLat || !customerLng) {
    return null;
  }

  const destinationAddress = dropAddress ? formatAddress(dropAddress) : "";
  const sourceAddress = pickupAddress ? formatAddress(pickupAddress) : "";
  const orderInvoicePairs = extractOrderInvoicePairs(shipment);

  const destinationInfo: DestinationInfo = {
    customerName: dropAddress?.contactDetails?.name || "",
    customerPhone: dropAddress?.contactDetails?.phoneNumber || "",
    fullAddress: destinationAddress,
    destinationAddress,
    deliveryInstructions: shipment.deliveryBooking?.deliveryInstructions || "",
    shipmentId: shipment.shipmentId,
    orderNumbers: orderInvoicePairs.map((pair) => pair.orderNumber),
    invoiceNumbers: orderInvoicePairs
      .map((pair) => pair.invoiceNumber)
      .filter((inv): inv is string => inv !== null),
    orderInvoicePairs,
    vehicleType:
      shipment.deliveryTracking?.deliveryAgent?.vehicleDetails?.vehicleType,
    sourceLat: pickupAddress?.lat,
    sourceLng: pickupAddress?.lng,
    sourceName: pickupAddress?.contactDetails?.name || "Pickup Location",
    sourceAddress,
    sourceContactName: pickupAddress?.contactDetails?.name || "",
    sourceContactPhone: pickupAddress?.contactDetails?.phoneNumber || "",
  };

  return { destinationInfo, customerLat, customerLng };
}

// Invoice extraction utilities are now imported from shared module

const JOURNEY_STEPS: Array<{
  status: string;
  timelineKey: string | null;
  remarks: string;
}> = [
  {
    status: "Shipment Created",
    timelineKey: null,
    remarks: "Auto parts shipment created and orders consolidated",
  },
  {
    status: "Booked At",
    timelineKey: "bookedAt",
    remarks: "Delivery booking confirmed with courier partner",
  },
  {
    status: "Assigned to Agent",
    timelineKey: "assignedToAgentAt",
    remarks: "Shipment assigned to delivery agent",
  },
  {
    status: "Package Picked Up",
    timelineKey: "pickedUpAt",
    remarks: "Package collected by delivery partner",
  },
  {
    status: "Package Delivered",
    timelineKey: "deliveredAt",
    remarks: "Successfully delivered to customer",
  },
  {
    status: "Package Cancelled",
    timelineKey: "cancelledAt",
    remarks: "Shipment was cancelled",
  },
];

function buildStatusHistory(
  shipment: Shipment,
): ShipmentDetails["statusHistory"] {
  const createdAtDate = shipment.createdAt
    ? new Date(shipment.createdAt)
    : new Date();

  const timeline: Record<string, unknown> = {
    ...(shipment.deliveryTracking?.timeline as
      | Record<string, unknown>
      | undefined),
    bookedAt: shipment.deliveryBooking?.bookedAt,
  };

  return JOURNEY_STEPS.map((step) => {
    let timestamp = "-";
    let hasData = false;

    if (step.timelineKey === null) {
      timestamp = createdAtDate.toLocaleString();
      hasData = true;
    } else if (timeline && timeline[step.timelineKey]) {
      try {
        const eventDate = new Date(
          timeline[step.timelineKey] as string | number | Date,
        );
        timestamp = eventDate.toLocaleString();
        hasData = true;
      } catch {}
    }

    return {
      status: step.status,
      timestamp,
      remarks: step.remarks,
      hasData,
    };
  });
}

interface ShipmentsTableProps {
  shipments: Shipment[];
}

export const cancelableShipmentStatuses = [
  ShipmentStatus.CREATED,
  ShipmentStatus.REQUESTED,
  ShipmentStatus.ASSIGNED,
];

export const deletableShipmentStatuses = [
  ShipmentStatus.CREATED,
  ShipmentStatus.CANCELLED,
];

export default function ShipmentsTable({
  shipments: initialShipments,
}: ShipmentsTableProps) {
  const [selectedShipmentDetails, setSelectedShipmentDetails] =
    useState<ShipmentDetails | null>(null);
  const [trackingDetails, setTrackingDetails] =
    useState<TrackingDetails | null>(null);
  const [shipments, setShipments] = useState<Shipment[]>(initialShipments);
  const [trackingUrlStates, setTrackingUrlStates] = useState<
    Record<string, string>
  >({});
  const popoverCloseCallbacks = useRef<Record<string, () => void>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    isOpen: isOpenCancelShipmentDialogue,
    onOpen: onOpenCancelShipmentDialogue,
    onClose: onCloseCancelShipmentDialogue,
  } = useDisclosure();

  const {
    isOpen: isOpenMarkDeliveredShipmentDialogue,
    onOpen: onOpenMarkDeliveredShipmentDialogue,
    onClose: onCloseMarkDeliveredShipmentDialogue,
  } = useDisclosure();

  const {
    isOpen: isOpenDeleteShipmentDialogue,
    onOpen: onOpenDeleteShipmentDialogue,
    onClose: onCloseDeleteShipmentDialogue,
  } = useDisclosure();

  const {
    isOpen: isShipmentModalOpen,
    onOpen: onOpenShipmentModal,
    onClose: onCloseShipmentModal,
  } = useDisclosure();

  const [shipmentIdToAction, setShipmentIdToAction] = useState<string>("");
  const [isSelfDispatch, setIsSelfDispatch] = useState<boolean>(false);
  const bgColor = useColorModeValue("white", "gray.800");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const [refetchCooldowns, setRefetchCooldowns] = useState<
    Record<string, number>
  >({});
  const { data: mobileConfigs } = useGetMobileConfig();
  const cooldownTime =
    Number(mobileConfigs?.orca_shipment_refetch_cooldown_seconds) * 1000 ||
    10000;
  const [printingShipmentId, setPrintingShipmentId] = useState<string | null>(
    null,
  );

  const toast = useToast();

  const renderActionButton = (
    isVisible: boolean,
    opts: {
      label: string;
      ariaLabel: string;
      icon: JSX.Element;
      hoverColor: string;
      hoverBg: string;
      onClick: (e: React.MouseEvent) => void;
      isDisabled?: boolean;
      isLoading?: boolean;
    },
  ) => {
    if (!isVisible) return null;
    return (
      <Tooltip label={opts.label}>
        <IconButton
          aria-label={opts.ariaLabel}
          icon={opts.icon}
          size="sm"
          variant="ghost"
          color="gray.400"
          _hover={{
            color: opts.hoverColor,
            transform: "scale(1.15)",
            bg: opts.hoverBg,
          }}
          transition="all 0.2s ease"
          onClick={opts.onClick}
          isDisabled={opts.isDisabled}
          isLoading={opts.isLoading}
        />
      </Tooltip>
    );
  };

  const renderTrackingUrlPopover = (shipment: Shipment) => {
    const isVisible =
      !shipment.deliveryBooking?.providerOrderId &&
      shipment.status !== ShipmentStatus.DELIVERED &&
      shipment.status !== ShipmentStatus.CANCELLED;

    if (!isVisible) return null;

    const currentUrl =
      trackingUrlStates[shipment.shipmentId] ??
      shipment.deliveryBooking?.trackingUrl ??
      "";

    return (
      <Popover
        placement="bottom"
        onClose={() => {
          // Clear temporary state when popover closes (Cancel, outside click, Escape)
          setTrackingUrlStates((prev) => {
            const newState = { ...prev };
            delete newState[shipment.shipmentId];
            return newState;
          });
          delete popoverCloseCallbacks.current[shipment.shipmentId];
        }}
      >
        {({ isOpen, onClose }) => {
          // Register close callback when popover opens
          if (isOpen && !popoverCloseCallbacks.current[shipment.shipmentId]) {
            popoverCloseCallbacks.current[shipment.shipmentId] = onClose;
          }

          return (
            <>
              <PopoverTrigger>
                <Box display="inline-block">
                  <Tooltip label="Add Tracking URL" isDisabled={isOpen}>
                    <IconButton
                      aria-label="Add Tracking URL"
                      icon={<FaLink />}
                      size="sm"
                      variant="ghost"
                      color="gray.400"
                      _hover={{
                        color: "purple.600",
                        transform: "scale(1.15)",
                        bg: "purple.50",
                      }}
                      transition="all 0.2s ease"
                    />
                  </Tooltip>
                </Box>
              </PopoverTrigger>
              <PopoverContent maxW="350px" onClick={(e) => e.stopPropagation()}>
                <PopoverHeader fontWeight="medium">Tracking URL</PopoverHeader>
                <PopoverBody>
                  <VStack spacing={3} align="stretch">
                    <Input
                      size="sm"
                      placeholder="Enter tracking URL"
                      value={currentUrl}
                      onChange={(e) => {
                        setTrackingUrlStates((prev) => ({
                          ...prev,
                          [shipment.shipmentId]: e.target.value,
                        }));
                      }}
                    />
                    <HStack spacing={2} justifyContent="flex-end">
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="blue"
                        isDisabled={!currentUrl.trim()}
                        onClick={async (e) => {
                          e.stopPropagation();
                          await handleTrackingUrlSubmit(
                            shipment.shipmentId,
                            currentUrl,
                            onClose,
                          );
                        }}
                      >
                        Confirm
                      </Button>
                    </HStack>
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </>
          );
        }}
      </Popover>
    );
  };

  const getTrackingDetails = (shipment: Shipment): TrackingDetails => ({
    trackingUrl: shipment.deliveryBooking?.trackingUrl || "",
    awbNo: shipment.deliveryBooking?.providerOrderId || "",
    shipmentNo: shipment.shipmentId,
    status: shipment.deliveryTracking?.trackingStatus,
    lastUpdatedAt: shipment.deliveryTracking?.lastUpdatedAt,
  });

  useEffect(() => {
    setShipments(initialShipments);
  }, [initialShipments]);

  useEffect(() => {
    const handleScroll = () => {
      Object.values(popoverCloseCallbacks.current).forEach((closeCallback) => {
        closeCallback();
      });
    };

    const scrollContainer = scrollContainerRef.current?.parentElement;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const handleViewShipment = async (shipment: Shipment) => {
    let latestShipment: Shipment = shipment;
    try {
      latestShipment = await getShipment(shipment.shipmentId, {
        populate: ["orders", "supplier", "customer"],
      });
    } catch (err: any) {
      toast({
        title: "Fetch Shipment ",
        description: err?.errorMsg,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    const createdAtDate = latestShipment.createdAt
      ? new Date(latestShipment.createdAt)
      : new Date();
    const primaryDropAddress = getPrimaryDropAddress(latestShipment);
    const fallbackDestination = `${primaryDropAddress?.streetAddress1}, ${primaryDropAddress?.city}, ${primaryDropAddress?.state}`;
    const multiCustomer = isMultiCustomerTrip(latestShipment);
    const shipmentDetails: ShipmentDetails = {
      id: latestShipment.shipmentId,
      shipmentNo: latestShipment.shipmentId,
      orderNo: latestShipment.orderNumbers?.join(", "),
      date: createdAtDate.toLocaleDateString() || "",
      customerName: multiCustomer
        ? getMultiCustomerLabel(latestShipment.customerIds)
        : primaryDropAddress?.contactDetails?.name,
      customerType: "Auto Parts Dealer",
      destinationAddress: multiCustomer
        ? formatMultiStopDestinationAddress(
            latestShipment,
            fallbackDestination,
          )
        : fallbackDestination,
      pickupAddress: `${latestShipment.pickupDetails?.address?.streetAddress1}, ${latestShipment.pickupDetails?.address?.city}, ${latestShipment.pickupDetails?.address?.state}`,
      courier: "Porter",
      trackingNo: latestShipment.deliveryBooking?.providerOrderId,
      trackingUrl: latestShipment.deliveryBooking?.trackingUrl,
      status: latestShipment.status,
      statusHistory: buildStatusHistory(latestShipment),
      lastUpdatedAt: latestShipment.deliveryTracking?.lastUpdatedAt
        ? new Date(
            latestShipment.deliveryTracking.lastUpdatedAt as unknown as string,
          ).toLocaleString()
        : "-",
      estimatedDelivery: latestShipment.deliveryBooking?.estimatedDeliveryTime
        ? (() => {
            try {
              return new Date(
                latestShipment.deliveryBooking.estimatedDeliveryTime,
              ).toLocaleDateString();
            } catch {
              return "Invalid date";
            }
          })()
        : "Not available",
      actualDelivery: latestShipment.deliveryTracking?.timeline?.deliveredAt
        ? (() => {
            try {
              return new Date(
                latestShipment.deliveryTracking.timeline.deliveredAt,
              ).toLocaleDateString();
            } catch {
              return "Invalid date";
            }
          })()
        : "-",
      deliveryInstructions:
        latestShipment.deliveryBooking?.deliveryInstructions || "-",
      packageDetails: {
        orderCount: latestShipment.orderNumbers?.length || 0,
        totalItemsQuantity: latestShipment.totalQuantity || 0,
      },
      billingDetails: {
        shippingCharges:
          latestShipment.deliveryBooking?.estimatedFare?.minorAmount || 0,
      },
      contactDetails: {
        senderName: latestShipment.pickupDetails?.address?.contactDetails?.name,
        senderPhone:
          latestShipment.pickupDetails?.address?.contactDetails?.phoneNumber,
        receiverName: primaryDropAddress?.contactDetails?.name,
        receiverPhone: primaryDropAddress?.contactDetails?.phoneNumber,
      },
      orders:
        (latestShipment as any).orders?.length > 0
          ? (latestShipment as any).orders.map((ord: any, index: number) => {
              const inv = ord?.invoiceInfo;
              const challan = ord?.challanInfo;
              const activeSupplierAllocation = ord?.allocatedTo?.find(
                (allocation: any) => allocation.isActive,
              );

              const supplierInv = activeSupplierAllocation?.invoiceInfo;
              const supplierChallan = activeSupplierAllocation?.challanInfo;
              const garaazInvoiceMediaId =
                inv?.invoiceMediaId?.$oid || inv?.invoiceMediaId;
              const supplierInvoiceMediaId =
                supplierInv?.invoiceMediaId?.$oid ||
                supplierInv?.invoiceMediaId;
              const garaazChallanMediaId =
                challan?.challanMediaId?.$oid || challan?.challanMediaId;
              const supplierChallanMediaId =
                supplierChallan?.challanMediaId?.$oid ||
                supplierChallan?.challanMediaId;

              return {
                id: `${latestShipment.shipmentId}-order-${index}`,
                orderNo: ord.orderNo,
                date: ord.orderDate
                  ? new Date(ord.orderDate).toLocaleDateString()
                  : "-",
                customerName:
                  primaryDropAddress?.contactDetails?.name || "-",
                branchName: "-",
                deliveryMode: ord.deliveryMode || "-",
                invoiceNo: inv?.invoiceNo,
                invoiceMediaId: garaazInvoiceMediaId || "-",
                invoiceAmount: Number(inv?.invoiceAmount) || 0,
                invoiceStatus: inv?.invoiceStatus || InvoiceStatus.UNPAID,
                challanNo: challan?.challanNo,
                challanMediaId: garaazChallanMediaId || "-",
                challanAmount: Number(challan?.challanAmount) || 0,
                paymentMode: inv?.payment?.mode || "-",
                paymentCollectedBy: inv?.payment?.collectedBy || "-",
                paymentCollectedAt: inv?.payment?.collectedAt || "-",
                supplierInvoiceNo: supplierInv?.invoiceNo,
                supplierInvoiceMediaId: supplierInvoiceMediaId || "-",
                supplierInvoiceAmount: Number(supplierInv?.invoiceAmount) || 0,
                supplierChallanNo: supplierChallan?.challanNo,
                supplierChallanMediaId: supplierChallanMediaId || "-",
                supplierChallanAmount:
                  Number(supplierChallan?.challanAmount) || 0,
                items: Array.isArray(ord?.orderItems)
                  ? ord.orderItems.map((item: any) => ({
                      partId: item?.partId || "-",
                      rootPartNumber: item?.rootPartNumber || "-",
                      givenQuantity: item?.givenQuantity || 0,
                      orderedQuantity: item?.orderedQuantity || 0,
                    }))
                  : [],
                status: ord.orderStatus,
              } as ShipmentDetails["orders"][number];
            })
          : [],
      isMultiStopTrip: isMultiStopTrip(latestShipment),
    };

    setSelectedShipmentDetails(shipmentDetails);
    onOpenShipmentModal();
  };

  const updateShipment = async (shipmentId: string) => {
    try {
      const response = await getShipment(shipmentId);
      setShipments((prev) =>
        prev.map((shipment) =>
          shipment.shipmentId === shipmentId
            ? { ...shipment, ...response }
            : shipment,
        ),
      );
    } catch (error) {
      console.error("Failed to update shipment in updateShipment:", error);
      throw error;
    }
  };

  const handleRefetchShipment = async (shipmentId: string) => {
    const now = Date.now();
    const lastRefetch = refetchCooldowns[shipmentId] || 0;
    const timeSinceLastRefetch = now - lastRefetch;

    if (timeSinceLastRefetch < cooldownTime) {
      const remainingTime = Math.ceil(
        (cooldownTime - timeSinceLastRefetch) / 1000,
      );
      toast({
        title: "Refetch Cooldown",
        description: `Please wait ${remainingTime} seconds before refetching this shipment again.`,
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    try {
      setRefetchCooldowns((prev) => ({ ...prev, [shipmentId]: now }));

      try {
        await getTracking(shipmentId);
      } catch (error) {
        console.log("Failed to get tracking in handleRefetchShipment:", error);
      }

      await updateShipment(shipmentId);
      toast({
        title: `Shipment ${shipmentId} updated successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: "Failed to refetch shipment details.",
        description:
          error instanceof Error ? error.message : "Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      // Reset cooldown on error so user can retry
      setRefetchCooldowns((prev) => ({ ...prev, [shipmentId]: 0 }));
    }
  };

  const handleCancelShipment = async (shipmentId: string) => {
    try {
      const cancelResponse = await cancelShipment(shipmentId);
      if (cancelResponse.code !== 200) {
        throw new Error(cancelResponse.errorMsg);
      }

      toast({
        title: `Shipment ${shipmentId} cancelled successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      try {
        await updateShipment(shipmentId);
      } catch (error) {
        console.error(
          "Failed to update shipment in handleCancelShipment:",
          error,
        );
      }
    } catch (error) {
      console.error(
        "Failed to cancel shipment in handleCancelShipment:",
        error,
      );
      toast({
        title: `Failed to cancel shipment ${shipmentId}`,
        description:
          error instanceof Error ? error.message : "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleDeleteShipment = async (shipmentId: string) => {
    try {
      await deleteShipment(shipmentId);

      toast({
        title: `Shipment ${shipmentId} deleted successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      setShipments((prev) => prev.filter((s) => s.shipmentId !== shipmentId));
    } catch (error) {
      toast({
        title: `Failed to delete shipment ${shipmentId}`,
        description:
          error instanceof Error ? error.message : "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleUpdateShipmentStatus = async (
    shipmentId: string,
    status: ShipmentStatus.DELIVERED | ShipmentStatus.CANCELLED,
  ) => {
    try {
      await updateShipmentService(shipmentId, { status });
      toast({
        title: `Shipment ${shipmentId} marked as ${status}`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      try {
        await updateShipment(shipmentId);
      } catch (error) {
        console.error(
          `Failed to update shipment in handleUpdateShipmentStatus:`,
          error,
        );
      }
    } catch (error) {
      toast({
        title: `Failed to mark shipment ${shipmentId} as ${status}`,
        description:
          error instanceof Error ? error.message : "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const openCancelShipmentDialog = (
    shipmentId: string,
    isSelfDispatch: boolean = false,
  ) => {
    setShipmentIdToAction(shipmentId);
    setIsSelfDispatch(isSelfDispatch);
    onOpenCancelShipmentDialogue();
  };

  const openDeleteShipmentDialog = (shipmentId: string) => {
    setShipmentIdToAction(shipmentId);
    onOpenDeleteShipmentDialogue();
  };

  const openMarkDeliveredShipmentDialog = (
    shipmentId: string,
    isSelfDispatch: boolean = false,
  ) => {
    setShipmentIdToAction(shipmentId);
    setIsSelfDispatch(isSelfDispatch);
    onOpenMarkDeliveredShipmentDialogue();
  };

  const handleConfirmedCancelShipment = () => {
    if (isSelfDispatch) {
      handleUpdateShipmentStatus(shipmentIdToAction, ShipmentStatus.CANCELLED);
    } else {
      handleCancelShipment(shipmentIdToAction);
    }
    onCloseCancelShipmentDialogue();
    onCloseShipmentModal();
    setShipmentIdToAction("");
    setIsSelfDispatch(false);
  };

  const handleMarkConsignmentDelivered = async (
    shipmentId: string,
    orderNo: string
  ) => {
    try {
      await markConsignmentDeliveredService(shipmentId, orderNo);
      toast({
        title: `Order ${orderNo} marked as delivered`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      await updateShipment(shipmentId);
    } catch (error) {
      toast({
        title: `Failed to mark ${orderNo} as delivered`,
        description:
          error instanceof Error ? error.message : "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleConfirmedMarkDeliveredShipment = async () => {
    try {
      if (isSelfDispatch) {
        await updateShipmentService(shipmentIdToAction, {
          status: ShipmentStatus.DELIVERED,
          deliveryTracking: {
            timeline: {
              deliveredAt: new Date().toISOString(),
            },
          },
        });
        toast({
          title: `Shipment ${shipmentIdToAction} marked as DELIVERED`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        try {
          await updateShipment(shipmentIdToAction);
        } catch (error) {
          console.error(
            `Failed to update shipment in handleConfirmedMarkDeliveredShipment:`,
            error,
          );
        }
      } else {
        await handleUpdateShipmentStatus(
          shipmentIdToAction,
          ShipmentStatus.DELIVERED,
        );
      }
    } catch (error) {
      toast({
        title: `Failed to mark shipment ${shipmentIdToAction} as DELIVERED`,
        description:
          error instanceof Error ? error.message : "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      onCloseMarkDeliveredShipmentDialogue();
      onCloseShipmentModal();
      setShipmentIdToAction("");
      setIsSelfDispatch(false);
    }
  };

  const handleConfirmedDeleteShipment = () => {
    handleDeleteShipment(shipmentIdToAction);
    onCloseDeleteShipmentDialogue();
    onCloseShipmentModal();
    setShipmentIdToAction("");
  };

  const getLocationMessage = useCallback(
    (shipment: Shipment): string | null => {
      const extracted = extractDestinationInfoFromShipment(shipment);
      if (
        !extracted ||
        extracted.customerLat === undefined ||
        extracted.customerLng === undefined
      ) {
        return null;
      }

      try {
        return generateLocationMessage(
          extracted.customerLat,
          extracted.customerLng,
          extracted.destinationInfo,
        );
      } catch (error) {
        console.error("Error generating location message:", error);
        return null;
      }
    },
    [],
  );

  /**
   * Handles sending location to driver via WhatsApp
   */
  const handleSendLocationToDriver = useCallback(
    (shipment: Shipment) => {
      const driverPhone = shipment.deliveryTracking?.deliveryAgent?.phoneNumber;

      if (!driverPhone) {
        toast({
          title: "Driver phone not available",
          description:
            "Driver phone number is not available for this shipment.",
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      const extracted = extractDestinationInfoFromShipment(shipment);
      if (
        !extracted ||
        extracted.customerLat === undefined ||
        extracted.customerLng === undefined
      ) {
        toast({
          title: "Location not available",
          description:
            "Customer location coordinates are not available for this shipment.",
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      try {
        openWhatsAppWithLocation(
          driverPhone,
          extracted.customerLat,
          extracted.customerLng,
          extracted.destinationInfo,
        );
      } catch (error) {
        toast({
          title: "Failed to send location",
          description:
            error instanceof Error ? error.message : "Please try again later.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    },
    [toast],
  );

  const handleTrackingUrlSubmit = async (
    shipmentId: string,
    trackingUrl: string,
    onClose: () => void,
  ) => {
    const trimmedUrl = trackingUrl.trim();

    if (!trimmedUrl) {
      return;
    }

    try {
      await updateShipmentService(shipmentId, {
        deliveryBooking: {
          trackingUrl: trimmedUrl,
        },
      });

      toast({
        title: "Tracking URL updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      await updateShipment(shipmentId);

      setTrackingUrlStates((prev) => {
        const newState = { ...prev };
        delete newState[shipmentId];
        return newState;
      });

      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errorMsg ||
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong";

      toast({
        title: "Failed to update tracking URL",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const fetchShipmentInvoiceMediaIds = useCallback(
    async (shipmentId: string): Promise<InvoiceMediaIdentifier[]> => {
      const latestShipment = await getShipment(shipmentId, {
        populate: ["orders"],
      });
      return extractInvoiceMediaIdsFromShipment(latestShipment);
    },
    [],
  );

  const handlePrintShipmentInvoices = useCallback(
    async (shipment: Shipment) => {
      setPrintingShipmentId(shipment.shipmentId);
      try {
        let invoiceMediaIds = extractInvoiceMediaIdsFromShipment(shipment);
        if (invoiceMediaIds.length === 0) {
          invoiceMediaIds = await fetchShipmentInvoiceMediaIds(
            shipment.shipmentId,
          );
        }

        const result = await printGaraazInvoices(invoiceMediaIds);
        showInvoicePrintSuccess(result.printableCount, toast);
      } catch (error) {
        handleInvoicePrintError(error, toast);
      } finally {
        setPrintingShipmentId(null);
      }
    },
    [fetchShipmentInvoiceMediaIds, toast],
  );

  return (
    <Box bg={bgColor} ref={scrollContainerRef} overflow={"hidden"}>
      <Box
        maxH="60"
        overflowY="auto"
        overflowX="auto"
        onWheel={(e) => e.stopPropagation()}
      >
        <Table
          variant="unstyled"
          size="sm"
          width="100%"
          sx={{
            width: "100%",
          }}
        >
          <Thead
            bg="linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)"
            position="sticky"
            top="0"
            zIndex="1"
          >
            <Tr>
              <Th
                px={6}
                py={3}
                fontSize="xs"
                fontWeight="medium"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                <HStack spacing={2}>
                  <Icon as={FaTruck} boxSize={3.5} color="orange.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="0.025em"
                  >
                    Shipment Details
                  </Text>
                </HStack>
              </Th>
              <Th
                px={6}
                py={3}
                fontSize="xs"
                fontWeight="medium"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
                textAlign={"left"}
              >
                <HStack spacing={2} justifyContent={"flex-start"}>
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="0.025em"
                  >
                    # Order Numbers
                  </Text>
                </HStack>
              </Th>
              <Th
                px={6}
                py={3}
                fontSize="xs"
                fontWeight="medium"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
                textAlign={"left"}
              >
                <HStack spacing={2} justifyContent={"flex-start"}>
                  <Icon as={FaFileInvoice} boxSize={3} color="purple.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="0.025em"
                  >
                    # Supplier Invoices
                  </Text>
                </HStack>
              </Th>
              <Th
                px={6}
                py={3}
                fontSize="xs"
                fontWeight="medium"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
                textAlign={"left"}
              >
                <HStack spacing={2} justifyContent={"flex-start"}>
                  <Icon as={FaFileInvoice} boxSize={3} color="purple.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="0.025em"
                  >
                    # Garaaz Invoices
                  </Text>
                </HStack>
              </Th>
              <Th
                px={6}
                py={3}
                fontSize="xs"
                fontWeight="medium"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
                textAlign={"left"}
              >
                <HStack spacing={2}>
                  <Icon as={FaBox} boxSize={3} color="blue.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="0.025em"
                  >
                    Total Items & Value
                  </Text>
                </HStack>
              </Th>
              <Th
                px={6}
                py={3}
                fontSize="xs"
                fontWeight="medium"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
                textAlign={"left"}
              >
                <HStack spacing={2}>
                  <Icon as={FaTruck} boxSize={3.5} color="green.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="0.025em"
                  >
                    Status
                  </Text>
                </HStack>
              </Th>
              <Th
                px={6}
                py={3}
                fontSize="xs"
                fontWeight="medium"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
                textAlign={"left"}
              >
                <HStack spacing={2} justifyContent={"flex-start"}>
                  <Icon as={MdAccessTime} boxSize={3.5} color="yellow.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="0.025em"
                  >
                    Pickup & Delivery Time
                  </Text>
                </HStack>
              </Th>
              <Th
                px={6}
                py={3}
                fontSize="xs"
                fontWeight="medium"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
                textAlign={"left"}
              >
                <HStack spacing={2} justifyContent={"flex-start"}>
                  <Icon as={FaUser} boxSize={3.5} color="blue.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="0.025em"
                  >
                    Rider Detail
                  </Text>
                </HStack>
              </Th>
              <Th
                px={6}
                py={3}
                fontSize="xs"
                fontWeight="medium"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
                textAlign={"left"}
              >
                <HStack spacing={2} justifyContent={"flex-start"}>
                  <Icon as={FaMoneyBill} boxSize={3.5} color="purple.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="0.025em"
                  >
                    Total Fare Amount
                  </Text>
                </HStack>
              </Th>
              <Th
                px={6}
                py={3}
                fontSize="xs"
                fontWeight="medium"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
                textAlign={"left"}
              >
                <HStack spacing={2} justifyContent={"flex-start"}>
                  <Icon as={FaEllipsisH} boxSize={3.5} color="red.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="0.025em"
                  >
                    Actions
                  </Text>
                </HStack>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {shipments
              .map((shipment) => {
                const locationMessage =
                  shipment.status !== ShipmentStatus.DELIVERED &&
                  shipment.status !== ShipmentStatus.CANCELLED
                    ? getLocationMessage(shipment)
                    : null;

                const createdAt = `${formatTime(
                  shipment.createdAt,
                )} - ${formatDateForNotification(shipment.createdAt)}`;
                const estimatedPickupTime = shipment.deliveryBooking
                  ?.estimatedPickupTime
                  ? `${formatTime(
                      shipment.deliveryBooking?.estimatedPickupTime,
                    )} - ${formatDateForNotification(
                      shipment.deliveryBooking?.estimatedPickupTime,
                    )}`
                  : "-";
                const pickupTime = shipment.deliveryTracking?.timeline
                  ?.pickedUpAt
                  ? `${formatTime(
                      shipment.deliveryTracking?.timeline?.pickedUpAt,
                    )} - ${formatDateForNotification(
                      shipment.deliveryTracking?.timeline?.pickedUpAt,
                    )}`
                  : "-";
                const deliveryTime = shipment.deliveryTracking?.timeline
                  ?.deliveredAt
                  ? `${formatTime(
                      shipment.deliveryTracking?.timeline?.deliveredAt,
                    )} - ${formatDateForNotification(
                      shipment.deliveryTracking?.timeline?.deliveredAt,
                    )}`
                  : "-";

                const estimatedFare = shipment.deliveryBooking?.estimatedFare
                  ? formatCurrency(
                      (shipment.deliveryBooking.estimatedFare.minorAmount ||
                        0) / 100,
                    )
                  : "-";
                const actualFare = shipment.deliveryBooking?.actualFare
                  ? formatCurrency(
                      (shipment.deliveryBooking.actualFare.minorAmount || 0) /
                        100,
                    )
                  : "-";
                const deliveryMode = (shipment as any).deliveryMode as
                  | DeliveryModes
                  | undefined;
                const displayShipmentId = (() => {
                  const id = shipment.shipmentId;
                  if (id.length <= 8) return id; // If too short, show as is

                  const start = id.substring(0, 6);
                  const end = id.substring(id.length - 10);
                  return `${start}....${end}`;
                })();

                const rows = [
                  <Tr
                    key={`shipment-${shipment.shipmentId}`}
                    _hover={{ bg: hoverBg }}
                  >
                    {/* Shipment Details */}
                    <Td px={6} py={4}>
                      <VStack align="start" spacing={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color="blue.600"
                          title={shipment.shipmentId}
                          display={"flex"}
                          flexDirection={"row"}
                          gap={1}
                        >
                          <Text fontSize="sm" color="gray.600" mt={-0.5}>
                            {displayShipmentId}
                          </Text>
                          <GaraazCopyText
                            text={shipment.shipmentId}
                            popOverMessage={"Copied"}
                          />
                        </Text>
                        {shipment.deliveryBooking?.providerOrderId && (
                          <Text
                            fontSize="xs"
                            fontWeight="medium"
                            color="gray.600"
                            title={shipment.deliveryBooking?.providerOrderId}
                            display={"flex"}
                            flexDirection={"row"}
                            gap={1}
                            mt={1}
                          >
                            <Text fontSize="xs" color="gray.600" mt={-1}>
                              {shipment.deliveryBooking?.providerOrderId}
                            </Text>
                            <GaraazCopyText
                              text={shipment.deliveryBooking?.providerOrderId}
                              popOverMessage={"Copied"}
                            />
                          </Text>
                        )}
                        <Text fontSize="xs" color="gray.500">
                          {createdAt}
                        </Text>
                        {deliveryMode && (
                          <DeliveryModeBadge mode={deliveryMode} />
                        )}
                      </VStack>
                    </Td>

                    {/* Order Numbers */}
                    <Td px={6} py={4} textAlign="left">
                      <VStack spacing={1} align="start">
                        {shipment.orderNumbers?.map((order, index) => {
                          const podStatus = getDeliveryStopStatus(
                            shipment,
                            order,
                          );
                          const isDelivered = podStatus === "DELIVERED";

                          return (
                          <Box
                            key={index}
                            flexDirection={"row"}
                            display={"flex"}
                            gap={1}
                            mt={1}
                            alignItems="center"
                          >
                            <Text fontSize="xs" color="gray.600" mt={-0.5}>
                              {order}
                            </Text>
                            {isMultiStopTrip(shipment) && (
                              <Text
                                fontSize="xs"
                                color={isDelivered ? "green.600" : "orange.600"}
                                fontWeight="600"
                              >
                                {isDelivered ? "Delivered" : "Pending"}
                              </Text>
                            )}
                            {isMultiStopTrip(shipment) &&
                              !isDelivered &&
                              shipment.status !== ShipmentStatus.DELIVERED &&
                              shipment.status !== ShipmentStatus.CANCELLED && (
                                <Button
                                  size="xs"
                                  colorScheme="green"
                                  variant="outline"
                                  cursor="pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkConsignmentDelivered(
                                      shipment.shipmentId,
                                      order
                                    );
                                  }}
                                >
                                  Mark Delivered
                                </Button>
                              )}
                            <GaraazCopyText
                              text={order}
                              popOverMessage={"Copied"}
                              key={index}
                            />
                          </Box>
                          );
                        })}
                      </VStack>
                    </Td>

                    <Td px={6} py={4} minW="300px" textAlign="left">
                      {(!shipment.supplierInvoiceNumbers ||
                        (Array.isArray(shipment.supplierInvoiceNumbers) &&
                          shipment.supplierInvoiceNumbers.length === 0)) &&
                        (!shipment.supplierChallanNumbers ||
                          (Array.isArray(shipment.supplierChallanNumbers) &&
                            shipment.supplierChallanNumbers.length === 0)) && (
                          <Text
                            fontSize="sm"
                            color="gray.400"
                            fontStyle="italic"
                          >
                            No invoice or Challan
                          </Text>
                        )}
                      <HStack spacing={0} align="start" width="100%">
                        {shipment.supplierInvoiceNumbers &&
                          shipment.supplierInvoiceNumbers.length > 0 && (
                            <VStack
                              align="start"
                              spacing={1}
                              flex={1}
                              minW="0"
                              pr={2}
                            >
                              {shipment?.supplierInvoiceNumbers?.map(
                                (invoice, index) => (
                                  <HStack key={`inv-${index}`} spacing={1}>
                                    <Text fontSize="xs" color="gray.600">
                                      {invoice}
                                    </Text>
                                    <GaraazCopyText
                                      text={invoice}
                                      popOverMessage={"Invoice Copied"}
                                      key={`copy-inv-${index}`}
                                    />
                                  </HStack>
                                ),
                              )}
                            </VStack>
                          )}
                        {shipment.supplierInvoiceNumbers &&
                          shipment.supplierInvoiceNumbers.length > 0 &&
                          shipment.supplierChallanNumbers &&
                          shipment.supplierChallanNumbers.length > 0 && (
                            <Box
                              width="1px"
                              bg="gray.300"
                              alignSelf="stretch"
                              minH="20px"
                              flexShrink={0}
                            />
                          )}

                        {shipment.supplierChallanNumbers &&
                          shipment.supplierChallanNumbers.length > 0 && (
                            <VStack
                              align="start"
                              spacing={1}
                              flex={1}
                              minW="0"
                              pl={2}
                            >
                              <Text
                                fontSize="xs"
                                fontWeight="900"
                                color="gray.600"
                              >
                                Challan Numbers
                              </Text>

                              {shipment?.supplierChallanNumbers?.map(
                                (challan, index) => (
                                  <>
                                    <HStack
                                      key={`challan-${index}`}
                                      spacing={1}
                                    >
                                      <Text fontSize="xs" color="gray.600">
                                        {challan}
                                      </Text>
                                      <GaraazCopyText
                                        text={challan}
                                        popOverMessage={"Challan Copied"}
                                        key={`copy-challan-${index}`}
                                      />
                                    </HStack>
                                  </>
                                ),
                              )}
                            </VStack>
                          )}
                      </HStack>
                    </Td>
                    <Td px={6} py={4} textAlign="left" minW="300px">
                      {(!shipment.invoiceNumbers ||
                        (Array.isArray(shipment.invoiceNumbers) &&
                          shipment.invoiceNumbers.length === 0)) &&
                        (!shipment.challanNumbers ||
                          (Array.isArray(shipment.challanNumbers) &&
                            shipment.challanNumbers.length === 0)) && (
                          <Text
                            fontSize="sm"
                            color="gray.400"
                            fontStyle="italic"
                          >
                            No invoice or Challan
                          </Text>
                        )}
                      <HStack spacing={0} align="start" flex={1} width="100%">
                        {shipment.invoiceNumbers &&
                          shipment.invoiceNumbers.length > 0 && (
                            <VStack
                              align="start"
                              spacing={1}
                              flex={1}
                              minW="0"
                              pr={2}
                            >
                              {shipment.invoiceNumbers.map((invoice, index) => (
                                <HStack key={`inv-${index}`} spacing={1}>
                                  <Text fontSize="xs" color="gray.600">
                                    {invoice}
                                  </Text>
                                  <GaraazCopyText
                                    text={invoice}
                                    popOverMessage="Invoice Copied"
                                    key={`copy-inv-${index}`}
                                  />
                                  {shipment.invoiceStatus &&
                                    shipment.invoiceStatus.length ===
                                      shipment.invoiceNumbers?.length &&
                                    shipment.invoiceStatus[index] && (
                                      <Tooltip
                                        label={shipment.invoiceStatus[index]}
                                        fontSize="xs"
                                        hasArrow
                                      >
                                        <Text
                                          fontSize="sm"
                                          fontWeight="bold"
                                          color={
                                            shipment.invoiceStatus[index] ===
                                            InvoiceStatus.PAID
                                              ? "green.600"
                                              : "red.600"
                                          }
                                        >
                                          ₹
                                        </Text>
                                      </Tooltip>
                                    )}
                                </HStack>
                              ))}
                            </VStack>
                          )}
                        {shipment.invoiceNumbers &&
                          shipment.invoiceNumbers.length > 0 &&
                          shipment.challanNumbers &&
                          shipment.challanNumbers.length > 0 && (
                            <Box
                              width="1px"
                              bg="gray.300"
                              alignSelf="stretch"
                              minH="20px"
                              flexShrink={0}
                            />
                          )}
                        {shipment.challanNumbers &&
                          shipment.challanNumbers.length > 0 && (
                            <VStack
                              align="start"
                              spacing={1}
                              flex={1}
                              minW="0"
                              pl={2}
                            >
                              <Text
                                fontSize="xs"
                                fontWeight="900"
                                color="gray.600"
                              >
                                Challan Numbers
                              </Text>

                              {shipment?.challanNumbers?.map(
                                (challan, index) => (
                                  <>
                                    <HStack
                                      key={`challan-${index}`}
                                      spacing={1}
                                    >
                                      <Text fontSize="xs" color="gray.600">
                                        {challan}
                                      </Text>
                                      <GaraazCopyText
                                        text={challan}
                                        popOverMessage="Challan Copied"
                                        key={`copy-challan-${index}`}
                                      />
                                    </HStack>
                                  </>
                                ),
                              )}
                            </VStack>
                          )}
                      </HStack>
                    </Td>

                    {/* Total Items & Value */}
                    <Td px={6} py={4} textAlign="left">
                      <VStack spacing={1} align="start">
                        <Text
                          fontSize="sm"
                          color="gray.900"
                          fontWeight="medium"
                          textAlign="left"
                        >
                          <Text as="span" color="blue.600" fontSize={"sm"}>
                            {shipment.totalItems || 0} items,
                            {shipment.totalQuantity || 0} Qty
                          </Text>
                        </Text>
                        <Text
                          fontSize="sm"
                          color="green.600"
                          fontWeight="semibold"
                          textAlign="left"
                        >
                          ₹{(shipment.totalValue || 0).toLocaleString()}
                        </Text>
                      </VStack>
                    </Td>

                    {/* Status */}
                    <Td px={6} py={4} textAlign="left">
                      <VStack align="start" spacing={1}>
                        <GetStatusBadge status={shipment.status} />
                      </VStack>
                    </Td>

                    {/* Pickup & Delivery */}
                    <Td px={6} py={4}>
                      <VStack spacing={1} align="start">
                        <Box
                          display="grid"
                          gridTemplateColumns="1fr auto 1.5fr"
                          gap={2}
                          w="100%"
                          alignItems="center"
                        >
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            fontWeight="medium"
                            textAlign="left"
                          >
                            Exp. Pickup
                          </Text>
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            textAlign="center"
                          >
                            :
                          </Text>
                          <Text fontSize="xs" color="gray.700">
                            {estimatedPickupTime}
                          </Text>

                          <Text
                            fontSize="xs"
                            color="gray.500"
                            fontWeight="medium"
                            textAlign="left"
                          >
                            PickedUp At
                          </Text>
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            textAlign="center"
                          >
                            :
                          </Text>
                          <Text fontSize="xs" color="gray.700">
                            {pickupTime}
                          </Text>

                          <Text
                            fontSize="xs"
                            color="gray.500"
                            fontWeight="medium"
                            textAlign="left"
                          >
                            Delivered At
                          </Text>
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            textAlign="center"
                          >
                            :
                          </Text>
                          <Text fontSize="xs" color="gray.700">
                            {deliveryTime}
                          </Text>
                        </Box>
                      </VStack>
                    </Td>

                    {/* Rider Detail */}
                    <Td px={6} py={4}>
                      <VStack spacing={1} align="start">
                        {shipment.deliveryTracking?.deliveryAgent ? (
                          <>
                            <Text
                              fontSize="xs"
                              color="gray.700"
                              fontWeight="medium"
                            >
                              {shipment.deliveryTracking.deliveryAgent.name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {
                                shipment.deliveryTracking.deliveryAgent
                                  .phoneNumber
                              }
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {
                                shipment.deliveryTracking.deliveryAgent
                                  .alternatePhoneNumber
                              }
                            </Text>

                            {/* Vehicle Details */}
                            {shipment.deliveryTracking.deliveryAgent
                              .vehicleDetails && (
                              <>
                                <HStack spacing={2} align="center">
                                  {shipment.deliveryTracking.deliveryAgent
                                    .vehicleDetails.vehicleType ===
                                    VehicleType.TWO_WHEELER && (
                                    <Icon
                                      as={FaMotorcycle}
                                      boxSize={3}
                                      color="blue.600"
                                      mt={0}
                                    />
                                  )}
                                  <Text fontSize="xs" color="gray.600">
                                    {shipment.deliveryTracking.deliveryAgent
                                      .vehicleDetails.vehicleNumber || "N/A"}
                                  </Text>
                                </HStack>
                              </>
                            )}
                          </>
                        ) : (
                          <Text fontSize="sm" color="gray.700">
                            -
                          </Text>
                        )}
                      </VStack>
                    </Td>

                    {/* Total Fare Amount */}
                    <Td px={6} py={4}>
                      <VStack spacing={1} align="start">
                        <Box
                          display="grid"
                          gridTemplateColumns="1fr auto 1fr"
                          gap={2}
                          w="100%"
                          alignItems="center"
                        >
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            fontWeight="medium"
                            textAlign="left"
                          >
                            Est. Fare
                          </Text>
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            textAlign="center"
                          >
                            :
                          </Text>
                          <Text fontSize="xs" color="gray.700">
                            {estimatedFare}
                          </Text>

                          <Text
                            fontSize="xs"
                            color="gray.500"
                            fontWeight="medium"
                            textAlign="left"
                          >
                            Actual Fare
                          </Text>
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            textAlign="center"
                          >
                            :
                          </Text>
                          <Text fontSize="xs" color="gray.700">
                            {actualFare}
                          </Text>
                        </Box>
                      </VStack>
                    </Td>

                    {/* Actions */}
                    <Td px={4} py={4}>
                      {(() => {
                        const actionButtons: (JSX.Element | null)[] = [];
                        actionButtons.push(
                          renderActionButton(true, {
                            label: "View Shipment Details",
                            ariaLabel: "View Details",
                            icon: <FaEye />,
                            hoverColor: "blue.600",
                            hoverBg: "blue.50",
                            onClick: (e) => {
                              e.stopPropagation();
                              handleViewShipment(shipment);
                            },
                          }),
                        );

                        const trackingUrlPopover =
                          renderTrackingUrlPopover(shipment);
                        if (trackingUrlPopover) {
                          actionButtons.push(trackingUrlPopover);
                        }

                        actionButtons.push(
                          renderActionButton(
                            Boolean(shipment.deliveryBooking?.trackingUrl),
                            {
                              label: "Track Shipment",
                              ariaLabel: "Track Shipment",
                              icon: <FaRoute />,
                              hoverColor: "green.600",
                              hoverBg: "green.50",
                              onClick: (e) => {
                                e.stopPropagation();
                                setTrackingDetails(
                                  getTrackingDetails(shipment),
                                );
                              },
                            },
                          ),
                        );

                        actionButtons.push(
                          renderActionButton(
                            !!(
                              shipment.status !== ShipmentStatus.DELIVERED &&
                              shipment.status !== ShipmentStatus.CANCELLED
                            ),
                            {
                              label: "Send Location to Driver",
                              ariaLabel: "Send Location to Driver",
                              icon: <FaMapMarkerAlt />,
                              hoverColor: "purple.600",
                              hoverBg: "purple.50",
                              onClick: (e) => {
                                e.stopPropagation();
                                handleSendLocationToDriver(shipment);
                              },
                            },
                          ),
                        );

                        actionButtons.push(
                          renderActionButton(true, {
                            label: "Print Garaaz Invoices",
                            ariaLabel: "Print Garaaz Invoices",
                            icon: <FaPrint />,
                            hoverColor: "purple.600",
                            hoverBg: "purple.50",
                            onClick: (e) => {
                              e.stopPropagation();
                              handlePrintShipmentInvoices(shipment);
                            },
                            isDisabled:
                              printingShipmentId === shipment.shipmentId,
                            isLoading:
                              printingShipmentId === shipment.shipmentId,
                          }),
                        );

                        actionButtons.push(
                          renderActionButton(
                            !!shipment.deliveryBooking?.providerOrderId,
                            {
                              label: "Refetch Details",
                              ariaLabel: "Refetch",
                              icon: <FaSync />,
                              hoverColor: "blue.600",
                              hoverBg: "blue.50",
                              onClick: (e) => {
                                e.stopPropagation();
                                handleRefetchShipment(shipment.shipmentId);
                              },
                            },
                          ),
                        );

                        actionButtons.push(
                          renderActionButton(
                            !isMultiStopTrip(shipment) &&
                              !shipment.deliveryBooking?.providerOrderId &&
                              shipment.status !== ShipmentStatus.DELIVERED &&
                              shipment.status !== ShipmentStatus.CANCELLED,
                            {
                              label: "Mark as Delivered",
                              ariaLabel: "Mark Delivered",
                              icon: <FcShipped color="gray.50" />,
                              hoverColor: "green.600",
                              hoverBg: "green.50",
                              onClick: (e) => {
                                e.stopPropagation();
                                openMarkDeliveredShipmentDialog(
                                  shipment.shipmentId,
                                  !shipment.deliveryBooking?.providerOrderId,
                                );
                              },
                            },
                          ),
                        );

                        actionButtons.push(
                          renderActionButton(
                            shipment.status !== ShipmentStatus.DELIVERED &&
                              shipment.status !== ShipmentStatus.CANCELLED &&
                              cancelableShipmentStatuses.includes(
                                shipment.status,
                              ),
                            {
                              label: shipment.deliveryBooking?.providerOrderId
                                ? "Cancel Shipment"
                                : "Mark as Cancelled",
                              ariaLabel: "Cancel",
                              icon: <FaTimes />,
                              hoverColor: "orange.600",
                              hoverBg: "orange.50",
                              onClick: (e) => {
                                e.stopPropagation();
                                openCancelShipmentDialog(
                                  shipment.shipmentId,
                                  !shipment.deliveryBooking?.providerOrderId,
                                );
                              },
                            },
                          ),
                        );

                        actionButtons.push(
                          renderActionButton(
                            deletableShipmentStatuses.includes(shipment.status),
                            {
                              label: "Delete Shipment",
                              ariaLabel: "Delete",
                              icon: <FaTrash />,
                              hoverColor: "red.600",
                              hoverBg: "red.50",
                              onClick: (e) => {
                                e.stopPropagation();
                                openDeleteShipmentDialog(shipment.shipmentId);
                              },
                            },
                          ),
                        );

                        if (locationMessage) {
                          actionButtons.push(
                            <GaraazCopyText
                              key="location-copy"
                              text={locationMessage}
                              popOverMessage="Location message copied"
                            />,
                          );
                        }

                        const visibleButtons = actionButtons.filter(
                          (btn) => btn !== null && btn !== undefined,
                        );

                        const rows: (JSX.Element | null)[][] = [];
                        for (let i = 0; i < visibleButtons.length; i += 3) {
                          rows.push(visibleButtons.slice(i, i + 3));
                        }

                        return (
                          <VStack spacing={1} align="stretch">
                            {rows.map((row, rowIndex) => (
                              <HStack
                                key={rowIndex}
                                spacing={1}
                                justifyContent="flex-start"
                              >
                                {row}
                              </HStack>
                            ))}
                          </VStack>
                        );
                      })()}
                    </Td>
                  </Tr>,
                ];

                return rows;
              })
              .flat()}
          </Tbody>
        </Table>
      </Box>
      {shipments.length === 0 && (
        <Box textAlign="center" py={12}>
          <Box
            w={16}
            h={16}
            display="flex"
            alignItems="center"
            justifyContent="center"
            mx="auto"
            mb={4}
            bg="gray.100"
            borderRadius="full"
          >
            <Icon as={FaTruck} boxSize={8} color="gray.400" />
          </Box>
          <Heading size="lg" color="gray.900" mb={2}>
            No shipments found
          </Heading>
          <Text color="gray.500">Try adjusting your search or filters</Text>
        </Box>
      )}

      <TrackingModal
        isOpen={!!trackingDetails}
        onClose={() => setTrackingDetails(null)}
        trackingDetails={
          trackingDetails || {
            trackingUrl: "",
            awbNo: "",
            shipmentNo: "",
            status: PorterDeliveryTrackingStatus.OPEN,
          }
        }
      />

      <GaraazAlertDialogue
        alertBodyText={`Are you sure you want to mark shipment ${shipmentIdToAction} as delivered? This action cannot be undone.`}
        alertDialogueHeaderText="Confirm Delivery"
        onSubmitBtn={handleConfirmedMarkDeliveredShipment}
        isOpen={isOpenMarkDeliveredShipmentDialogue}
        onClose={onCloseMarkDeliveredShipmentDialogue}
        onOpen={onOpenMarkDeliveredShipmentDialogue}
        submitBtnText="Mark as Delivered"
        leftBtnText="Cancel"
        cancelBtnColorScheme="gray"
      />

      <GaraazAlertDialogue
        alertBodyText={`Are you sure you want to cancel shipment ${shipmentIdToAction}? This action cannot be undone.`}
        alertDialogueHeaderText="Confirm Cancellation"
        onSubmitBtn={handleConfirmedCancelShipment}
        isOpen={isOpenCancelShipmentDialogue}
        onClose={onCloseCancelShipmentDialogue}
        onOpen={onOpenCancelShipmentDialogue}
        submitBtnText="Cancel Shipment"
        leftBtnText="Keep Shipment"
        submitBtnColorScheme="red"
        cancelBtnColorScheme="gray"
      />

      <GaraazAlertDialogue
        alertBodyText={`Are you sure you want to delete shipment ${shipmentIdToAction}? \nThis action cannot be undone.`}
        alertDialogueHeaderText="Confirm Deletion"
        onSubmitBtn={handleConfirmedDeleteShipment}
        isOpen={isOpenDeleteShipmentDialogue}
        onClose={onCloseDeleteShipmentDialogue}
        onOpen={onOpenDeleteShipmentDialogue}
        submitBtnText="Delete Shipment"
        leftBtnText="Keep Shipment"
        submitBtnColorScheme="red"
        cancelBtnColorScheme="gray"
      />

      <ShipmentDetailsModal
        isOpen={isShipmentModalOpen}
        onClose={onCloseShipmentModal}
        shipment={selectedShipmentDetails}
        onCancelShipment={(shipmentId, isSelfDispatch) => {
          openCancelShipmentDialog(shipmentId, isSelfDispatch);
        }}
        onDeleteShipment={(shipmentId) => {
          openDeleteShipmentDialog(shipmentId);
        }}
        onMarkDeliveredShipment={(shipmentId, isSelfDispatch) => {
          openMarkDeliveredShipmentDialog(shipmentId, isSelfDispatch);
        }}
      />
    </Box>
  );
}
